import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { apiClient } from "@/App";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { GraduationCap, Share2, Twitter, Linkedin, Copy, Check } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";

const SharePage = () => {
  const { shareId } = useParams();
  const [share, setShare] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const fetchShare = async () => {
      try {
        const response = await apiClient.get(`/share/${shareId}`);
        setShare(response.data);
      } catch (err) {
        console.error("Error fetching share:", err);
        setError("This share link is not available or has expired.");
      } finally {
        setLoading(false);
      }
    };

    if (shareId) {
      fetchShare();
    }
  }, [shareId]);

  const shareUrl = window.location.href;

  const copyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    toast.success("Link copied!");
    setTimeout(() => setCopied(false), 2000);
  };

  const shareOnTwitter = () => {
    const text = `🎉 ${share.user_name} got accepted to ${share.college_name}! #CollegeAcceptance #Admit List`;
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(shareUrl)}`, "_blank");
  };

  const shareOnLinkedIn = () => {
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`, "_blank");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-4">
              <GraduationCap className="w-8 h-8 text-destructive" />
            </div>
            <h2 className="text-xl font-semibold mb-2">Share Not Found</h2>
            <p className="text-muted-foreground">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background noise-bg flex flex-col items-center justify-center px-4 py-12">
      {/* Header */}
      <div className="flex items-center gap-2 mb-8">
        <GraduationCap className="w-8 h-8 text-primary" />
        <span className="font-bold text-xl font-outfit">Admit List</span>
      </div>

      {/* Share Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-lg"
      >
        <Card className="overflow-hidden">
          <div className="bg-gradient-to-br from-emerald-500 to-teal-600 p-8 text-white text-center">
            <div className="text-6xl mb-4">🎉</div>
            <h1 className="text-2xl md:text-3xl font-bold font-outfit mb-2">
              Congratulations!
            </h1>
            <p className="text-emerald-100">
              {share.user_name} has been accepted to
            </p>
          </div>
          <CardContent className="p-8 text-center">
            <h2 className="text-2xl md:text-3xl font-bold font-outfit mb-4">
              {share.college_name}
            </h2>
            {share.message && (
              <p className="text-muted-foreground italic mb-6">
                "{share.message}"
              </p>
            )}
            <div className="flex items-center justify-center gap-3 flex-wrap">
              <Button
                variant="outline"
                size="sm"
                onClick={copyLink}
                className="rounded-full"
                data-testid="copy-link-btn"
              >
                {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                {copied ? "Copied!" : "Copy Link"}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={shareOnTwitter}
                className="rounded-full"
                data-testid="share-twitter-btn"
              >
                <Twitter className="w-4 h-4 mr-2" />
                Twitter
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={shareOnLinkedIn}
                className="rounded-full"
                data-testid="share-linkedin-btn"
              >
                <Linkedin className="w-4 h-4 mr-2" />
                LinkedIn
              </Button>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* CTA */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.3 }}
        className="mt-8 text-center"
      >
        <p className="text-muted-foreground mb-4">
          Track your college applications with Admit List
        </p>
        <Button
          onClick={() => window.location.href = "/"}
          className="rounded-full"
          data-testid="get-started-btn"
        >
          Get Started Free
        </Button>
      </motion.div>
    </div>
  );
};

export default SharePage;
